import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  DatabaseBackup, Download, Upload, Trash2, CheckCircle2, AlertTriangle,
  ShieldAlert, Loader2, FileSpreadsheet, X, CheckSquare, Square, Info,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const PengaturanDataPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState({});
  const [exporting, setExporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);
  const [notif, setNotif] = useState(null); // {type, msg}

  const notify = (type, msg) => { setNotif({ type, msg }); setTimeout(() => setNotif(null), 6000); };

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/data/collections`, { withCredentials: true });
      setCollections(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { if (isAdmin) fetchCollections(); }, [isAdmin, fetchCollections]);

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto mt-10" data-testid="pengaturan-data-denied">
        <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-center">
          <ShieldAlert className="w-10 h-10 text-rose-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white">Akses Ditolak</h2>
          <p className="text-sm text-slate-400 mt-1">Menu Backup &amp; Reset Data hanya dapat diakses oleh <b className="text-rose-300">Owner / Super Admin</b>.</p>
        </div>
      </div>
    );
  }

  const totalRecords = collections.reduce((s, c) => s + (c.count || 0), 0);
  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = collections.length > 0 && selectedKeys.length === collections.length;

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(collections.map((c) => [c.key, true])));
  };
  const toggleOne = (k) => setSelected((s) => ({ ...s, [k]: !s[k] }));

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/data/export`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : "backup-data.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify("success", "Backup Excel berhasil diunduh.");
    } catch (err) {
      notify("error", err.response?.data?.detail || "Gagal mengekspor data");
    } finally { setExporting(false); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await axios.post(`${BACKEND_URL}/api/data/import`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      const detail = res.data?.detail || {};
      const total = Object.values(detail).reduce((a, b) => a + b, 0);
      notify("success", `${res.data.message} — ${total} baris dipulihkan pada ${Object.keys(detail).length} jenis data.`);
      setImportFile(null);
      fetchCollections();
    } catch (err) {
      notify("error", err.response?.data?.detail || "Gagal mengimpor data");
    } finally { setImporting(false); }
  };

  const handleWipe = async () => {
    setWiping(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/data/wipe`, { collections: selectedKeys }, { withCredentials: true });
      const detail = res.data?.detail || {};
      const total = Object.values(detail).reduce((a, b) => a + b, 0);
      notify("success", `${res.data.message} — ${total} baris terhapus.`);
      setShowWipeConfirm(false);
      setSelected({});
      fetchCollections();
    } catch (err) {
      notify("error", err.response?.data?.detail || "Gagal menghapus data");
    } finally { setWiping(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="pengaturan-data-page">
      {/* Header */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
          <DatabaseBackup className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Backup &amp; Reset Data</h1>
          <p className="text-xs text-slate-400">Ekspor / impor data ke Excel &amp; hapus data terpilih — khusus Owner / Super Admin</p>
        </div>
      </div>

      {notif && (
        <div data-testid="data-notif" className={`p-3 rounded-xl text-xs flex items-center gap-2 border ${notif.type === "success" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border-rose-500/30 text-rose-300"}`}>
          {notif.type === "success" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          <span>{notif.msg}</span>
        </div>
      )}

      {/* EXPORT */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center gap-2 mb-2">
          <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-bold text-white">Export Data ke Excel</h3>
        </div>
        <p className="text-[11px] text-slate-400 mb-4">Unduh seluruh data bisnis dalam satu file Excel (setiap jenis data menjadi sheet terpisah). File ini bisa dibaca langsung, dan dapat di-<b className="text-slate-200">upload ulang</b> untuk memulihkan data. Total saat ini: <b className="text-emerald-300" data-testid="total-records">{totalRecords.toLocaleString("id-ID")}</b> baris.</p>
        <button onClick={handleExport} disabled={exporting} data-testid="export-data-btn" className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-600/20 flex items-center gap-2">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {exporting ? "Menyiapkan file..." : "Download Backup Excel"}
        </button>
      </div>

      {/* IMPORT */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center gap-2 mb-2">
          <Upload className="w-4 h-4 text-cyan-400" />
          <h3 className="text-sm font-bold text-white">Import / Pulihkan Data dari Excel</h3>
        </div>
        <div className="p-2.5 mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Import akan <b>menimpa (replace)</b> data pada setiap jenis data yang ada di file. Gunakan file hasil Export dari aplikasi ini. Akun login &amp; pengaturan tidak terpengaruh.</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 border-dashed rounded-xl text-xs text-slate-300 cursor-pointer hover:border-cyan-500/50 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-slate-500" />
            <span className="truncate">{importFile ? importFile.name : "Pilih file Excel (.xlsx)…"}</span>
            <input type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="import-file-input" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
          </label>
          <button onClick={handleImport} disabled={!importFile || importing} data-testid="import-data-btn" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-xs font-bold rounded-xl flex items-center gap-2 justify-center">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {importing ? "Memulihkan..." : "Upload & Pulihkan"}
          </button>
        </div>
      </div>

      {/* WIPE / RESET */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-rose-500/30 shadow-xl">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="w-4 h-4 text-rose-400" />
          <h3 className="text-sm font-bold text-white">Hapus Data (Reset)</h3>
        </div>
        <p className="text-[11px] text-slate-400 mb-3">Pilih jenis data yang ingin dihapus permanen. Akun login &amp; akun kas inti (Cash/Bank/Dana Cadangan) tidak akan terhapus. <b className="text-rose-300">Tindakan ini tidak dapat dibatalkan</b> — sebaiknya Export dulu.</p>

        <div className="flex items-center justify-between mb-2">
          <button onClick={toggleAll} data-testid="select-all-btn" className="text-[11px] font-bold text-slate-300 hover:text-white flex items-center gap-1.5">
            {allSelected ? <CheckSquare className="w-4 h-4 text-rose-400" /> : <Square className="w-4 h-4 text-slate-500" />}
            {allSelected ? "Batal Pilih Semua" : "Pilih Semua"}
          </button>
          <span className="text-[11px] text-slate-500">{selectedKeys.length} jenis dipilih</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="wipe-collections-list">
          {collections.map((c) => (
            <button key={c.key} onClick={() => toggleOne(c.key)} data-testid={`wipe-item-${c.key}`}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-colors ${selected[c.key] ? "bg-rose-500/10 border-rose-500/40" : "bg-slate-900 border-slate-800 hover:border-slate-700"}`}>
              <span className="flex items-center gap-2 min-w-0">
                {selected[c.key] ? <CheckSquare className="w-4 h-4 text-rose-400 shrink-0" /> : <Square className="w-4 h-4 text-slate-600 shrink-0" />}
                <span className="text-xs text-slate-200 truncate">{c.label}</span>
              </span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 ${c.count > 0 ? "bg-slate-800 text-slate-300" : "bg-slate-800/50 text-slate-600"}`}>{c.count}</span>
            </button>
          ))}
        </div>

        <div className="pt-4 flex justify-end border-t border-slate-800 mt-4">
          <button onClick={() => setShowWipeConfirm(true)} disabled={selectedKeys.length === 0} data-testid="open-wipe-confirm-btn" className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Hapus Data Terpilih ({selectedKeys.length})
          </button>
        </div>
      </div>

      {/* Konfirmasi hapus */}
      {showWipeConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-rose-500/40 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4" data-testid="wipe-confirm-modal">
            <div className="flex items-center gap-2 text-rose-300">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="text-sm font-bold">Konfirmasi Hapus Data</h3>
            </div>
            <p className="text-xs text-slate-300">Anda akan menghapus <b className="text-rose-300">{selectedKeys.length} jenis data</b> secara permanen. Tindakan ini tidak dapat dibatalkan. Lanjutkan?</p>
            <div className="max-h-40 overflow-y-auto rounded-lg bg-slate-950 border border-slate-800 p-2 space-y-1">
              {selectedKeys.map((k) => {
                const c = collections.find((x) => x.key === k);
                return <div key={k} className="text-[11px] text-slate-400 flex justify-between"><span>{c?.label || k}</span><span className="font-mono text-slate-500">{c?.count ?? 0} baris</span></div>;
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowWipeConfirm(false)} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
              <button onClick={handleWipe} disabled={wiping} data-testid="confirm-wipe-btn" className="px-4 py-1.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-xl text-xs font-bold flex items-center gap-1.5">
                {wiping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Setuju, Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PengaturanDataPage;
